import { useEffect, useRef, useState } from 'react';

const differentials = [
  {
    icon: 'ri-user-heart-line',
    title: 'Atendimento Personalizado',
    description:
      'Cada cliente é único. Desenvolvemos estratégias jurídicas sob medida, com comunicação direta e transparente do início ao fim.',
  },
  {
    icon: 'ri-speed-up-line',
    title: 'Agilidade nos Processos',
    description:
      'Trabalhamos com eficiência para reduzir ao máximo o tempo dos processos, evitando atrasos desnecessários que prejudicam as famílias.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Transparência Total',
    description:
      'Você acompanha cada etapa do seu processo. Sem surpresas, sem jargões incompreensíveis — apenas clareza e honestidade.',
  },
  {
    icon: 'ri-safe-2-line',
    title: 'Especialização em Patrimônio',
    description:
      'Foco exclusivo em Direito Sucessório e Imobiliário garante profundidade técnica e resultados superiores para proteger seu patrimônio.',
  },
];

const testimonials = [
  {
    name: 'Sra. Claudia Ferreira',
    role: 'Inventário de imóvel familiar',
    city: 'São Caetano do Sul — SP',
    text: 'O Dr. Bruno Castro resolveu em poucos meses o inventário que minha família tentava regularizar há anos. Atendimento impecável, transparência em cada etapa. Recomendo com toda a confiança.',
    stars: 5,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Brazilian%20woman%20in%20her%2050s%2C%20elegant%20appearance%2C%20sophisticated%20style%2C%20warm%20smile%2C%20neutral%20light%20background%2C%20portrait%20photo%2C%20high%20quality&width=80&height=80&seq=test001&orientation=squarish',
  },
  {
    name: 'Sr. Ricardo Almeida',
    role: 'Regularização de imóvel',
    city: 'Santo André — SP',
    text: 'Tinha um imóvel com documentação irregular há décadas. Em menos de 6 meses, o escritório resolveu tudo. Equipe competente e extremamente atenciosa. Serviço de alto nível.',
    stars: 5,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Brazilian%20man%20in%20his%2050s%2C%20executive%20appearance%2C%20confident%20smile%2C%20neutral%20background%2C%20portrait%20photo%2C%20high%20quality&width=80&height=80&seq=test002&orientation=squarish',
  },
  {
    name: 'Dra. Patrícia Souza',
    role: 'Direito imobiliário',
    city: 'São Bernardo do Campo — SP',
    text: 'Precisei de assessoria jurídica em uma transação imobiliária de grande valor. O Dr. Bruno Castro foi fundamental para garantir segurança em todo o processo. Profissional excepcional.',
    stars: 5,
    avatar: 'https://readdy.ai/api/search-image?query=elegant%20professional%20Brazilian%20woman%20doctor%20in%20her%2040s%2C%20sophisticated%20appearance%2C%20warm%20genuine%20smile%2C%20soft%20neutral%20background%2C%20portrait%20photo&width=80&height=80&seq=test003&orientation=squarish',
  },
];

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(28px)';
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

export default function Differentials() {
  const sectionRef = useScrollReveal(0.05);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  return (
    <section id="diferenciais" className="py-28 bg-[#0F4A2B] relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 z-0 opacity-10">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20green%20marble%20texture%20with%20subtle%20gold%20veins%2C%20luxury%20stone%20surface%2C%20premium%20material%20background%2C%20elegant%20abstract%20pattern%2C%20high%20resolution&width=1920&height=1200&seq=diff001&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#C7A76C]/50 to-transparent z-10"></div>

      <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#C7A76C]"></div>
            <span className="font-sans text-[#C7A76C] text-xs font-medium tracking-widest uppercase">
              Por que nos escolher
            </span>
            <div className="w-8 h-px bg-[#C7A76C]"></div>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-white font-semibold leading-tight max-w-3xl mx-auto">
            O que nos torna{' '}
            <span className="text-[#C7A76C] italic">diferentes</span>
          </h2>
        </div>

        {/* Differentials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {differentials.map((item, i) => (
            <div
              key={item.title}
              className="group flex flex-col gap-3 md:gap-4 bg-white/5 border border-white/10 hover:border-[#C7A76C]/40 rounded-2xl p-7 transition-all duration-300"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              {/* Header: icon + title in a row on mobile, stacked on desktop */}
              <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-[#C7A76C]/15">
                  <i className={`${item.icon} text-[#C7A76C] text-xl md:text-2xl`}></i>
                </div>
                <h3 className="font-serif text-lg md:text-xl text-white font-semibold flex-1">{item.title}</h3>
                <div className="w-5 h-5 flex items-center justify-center md:hidden flex-shrink-0">
                  <i className="ri-arrow-down-s-line text-[#C7A76C] text-lg transition-transform duration-300 group-hover:rotate-180"></i>
                </div>
              </div>
              {/* Description: always visible on desktop, visible on hover on mobile */}
              <p className="font-sans text-white/60 text-sm leading-relaxed hidden group-hover:block md:block">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden mb-20">
          {[
            { number: '500+', label: 'Casos Resolvidos' },
            { number: '98%', label: 'Clientes Satisfeitos' },
            { number: '10+', label: 'Anos de Experiência' },
            { number: '6 meses', label: 'Tempo Médio de Resolução' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center justify-center gap-2 bg-[#0C3A22] py-10 px-6 text-center"
            >
              <span className="font-serif text-4xl text-[#C7A76C] font-bold">{stat.number}</span>
              <span className="font-sans text-white/60 text-xs tracking-wide">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div>
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="w-8 h-px bg-[#C7A76C]/50"></div>
            <span className="font-sans text-[#C7A76C] text-xs font-medium tracking-widest uppercase">
              Depoimentos
            </span>
            <div className="w-8 h-px bg-[#C7A76C]/50"></div>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10">
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {Array.from({ length: testimonials[activeTestimonial].stars }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#C7A76C] text-lg"></i>
                ))}
              </div>

              <p className="font-sans text-white/80 text-base leading-relaxed mb-8 italic">
                &ldquo;{testimonials[activeTestimonial].text}&rdquo;
              </p>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#C7A76C]/30 flex-shrink-0">
                  <img
                    src={testimonials[activeTestimonial].avatar}
                    alt={testimonials[activeTestimonial].name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-sans text-white font-semibold text-sm">
                    {testimonials[activeTestimonial].name}
                  </p>
                  <p className="font-sans text-[#C7A76C] text-xs">{testimonials[activeTestimonial].role}</p>
                  <p className="font-sans text-white/40 text-xs">{testimonials[activeTestimonial].city}</p>
                </div>
              </div>
            </div>

            {/* Testimonial navigation */}
            <div className="flex items-center justify-center gap-3 mt-6">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    i === activeTestimonial
                      ? 'w-8 h-2 bg-[#C7A76C]'
                      : 'w-2 h-2 bg-white/25 hover:bg-white/50'
                  }`}
                  aria-label={`Depoimento ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
